import { AccessSpace } from '@upright/common/iam/access/types';
import { type CompanyProductSetQuality } from '@upright/common/internal/product-resolver/types';
import { CompanyType } from '@upright/common/model/input/companies/types';
import { ModelEntityType } from '@upright/common/model/input/types';
import {
  AdditionalSearchType,
  type ModelEntitySearchResponseItem,
  type SearchEntityType,
} from '@upright/common/model/search/types';
import { friendlyCountryName } from '@upright/common/model/texts/friendly-country-name';
import { friendlyProductSetQuality } from '@upright/common/model/texts/friendly-product-set-quality';
import { Checkbox } from '@upright/ui/common/generic/components/checkbox';
import { Input } from '@upright/ui/common/generic/components/input';
import { Skeleton, SkeletonProvider } from '@upright/ui/common/generic/components/skeleton';
import { Text } from '@upright/ui/common/generic/components/typography';
import { getModelEntitySvgIcon } from '@upright/ui/common/generic/global/model-entity-icons';
import { startMenuCategories } from '@upright/ui/common/upright/internal/start-menu/menu-data';
import { useSuggestions } from '@upright/ui/common/upright/model/output/search/header-search/use-suggestions';
import {
  isSearchTriggeredOnInput,
  useEntitySearch,
} from '@upright/ui/common/upright/model/output/search/use-entity-search';
import { LayoutWindow10 } from '@upright/ui/icons';
import AiSparklesIcon from '@upright/ui/icons/ai-sparkles-streamline-core';
import InterfaceSearch from '@upright/ui/icons/img/streamline-mini-line/interface-essential/search/interface-search.svg';
import {
  friendlyCompanyType,
  friendlyGroupType,
  friendlyModelEntityType,
  uppercaseFirstLetter,
} from '@upright/ui/untidy/texts';
import clsx from 'clsx';
import { capitalize } from 'lodash';
import { type CSSProperties, type KeyboardEvent, useEffect, useMemo, useRef } from 'react';
import {
  Autocomplete,
  Dialog,
  Header,
  type Key,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Modal,
  ModalOverlay,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-aria-components';
import {
  askRowPosition,
  enterFallback,
  exampleHit,
  exampleSearchSelection,
  groupSearchHits,
  type InternalPageHit,
  internalPageHits,
  type SearchFilter,
  type SearchFilterId,
} from './unified-shell-search-results';
import styles from './unified-shell-search-dialog.module.css';

/** Where the top bar's field is, in viewport pixels: its top edge, and its right edge's distance from the right. */
export interface SearchPanelAnchor {
  top: number;
  right: number;
}

export interface SearchPanelProps {
  query: string;
  onQueryChange: (next: string) => void;
  filter: SearchFilterId;
  onFilterChange: (next: SearchFilterId) => void;
  filters: SearchFilter[];
  /** What Everything searches for this audience. */
  everythingScope: readonly SearchEntityType[];
  placeholder: string;
  /** What the list says before anything searchable is typed, when the chip has no examples to offer. */
  emptyHint: string;
  /** Whether the "Ask Upright AI" row is offered: only to users who can open a chat. */
  canAskAi: boolean;
  /**
   * The team's switch (PLT-7169): `undefined` for a customer, who never sees it. On, the panel adds
   * the team-only types, every fact the search returns per row, and the internal pages.
   */
  teamDetails: boolean | undefined;
  onTeamDetailsChange: (next: boolean) => void;
  onOpenHit: (hit: ModelEntitySearchResponseItem) => void;
  onOpenPage: (page: InternalPageHit) => void;
  onAsk: (query: string) => void;
}

interface Props extends SearchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** The field to open over. Absent when the field is not on screen, and then the panel takes the top of the window. */
  anchor: SearchPanelAnchor | undefined;
}

/**
 * The search panel the unified shell's top-bar field opens (PLT-7168, from the PLT-7118 prototype
 * at `/internal/proto/internal-search`): one panel for customers and the team.
 *
 * It opens where the field is, as Slack's and Gmail's do: the panel's own field lands on the bar's
 * and the panel grows down and leftwards, so it reads as the field getting bigger. Under the field
 * sits the type filter; under that the results, in sections by type, or the chip's example searches
 * while nothing is typed (PLT-8052); and, whenever something searchable is typed and the user can
 * chat, an "Ask Upright AI" row that hands the text to a new chat. Search itself never calls a model.
 *
 * The team's version is the customer's plus additions in fixed places, behind one switch in the
 * footer, never a different layout (DESIGN.md rule 44): the team-only types after a divider in the
 * filter row, every fact the search returns at the end of each row, and the internal pages as a
 * section of their own. With the switch off a team member sees what a customer sees.
 *
 * Built on `react-aria-components` like the shell's drawer (`unified-shell-drawer.tsx`): the overlay
 * gives focus trap, focus restore, Escape and dismiss-on-scrim, and `Autocomplete` keeps typing in
 * the field while the arrow keys move a virtual focus through the rows. What Enter opens with no row
 * highlighted is this file's own rule (`handleKeyDownCapture`). It is not the design system's
 * `Modal`, which is a centred box with a title and a close button; this is anchored to a control and
 * has neither.
 *
 * `data-color-mode` / `data-light-theme` are repeated on the overlay for the reason `Modal.tsx`
 * gives: the overlay is portalled out of the `ThemeProvider` that declares Primer's colour variables.
 */
export function UnifiedShellSearchDialog({ isOpen, onClose, anchor, ...panelProps }: Props) {
  return (
    <ModalOverlay
      className={styles.overlay}
      data-color-mode="light"
      data-light-theme="light"
      isDismissable
      isOpen={isOpen}
      onOpenChange={next => {
        if (!next) onClose();
      }}
    >
      <Modal
        className={clsx(styles.panel, panelProps.teamDetails === true && styles.panelWide)}
        style={anchorStyle(anchor)}
      >
        <Dialog aria-label="Search" className={styles.dialog}>
          <SearchPanel {...panelProps} />
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

function anchorStyle(anchor: SearchPanelAnchor | undefined): CSSProperties | undefined {
  if (anchor === undefined) {
    return undefined;
  }
  // Read by `.panel` in the stylesheet, which falls back to the top of the window without them.
  const style: CSSProperties & Record<'--anchor-top' | '--anchor-right', string> = {
    '--anchor-top': `${anchor.top}px`,
    '--anchor-right': `${anchor.right}px`,
  };
  return style;
}

/**
 * Ids repeat across entity types (a legal entity and a company can carry the same one, which is why
 * the search's own de-duplication compares type and id together), so the row key has to as well.
 */
function hitKey(hit: ModelEntitySearchResponseItem) {
  return `${hit.type}:${hit.id}`;
}

const ASK_ROW_KEY = 'ask-ai';

/** Internal pages are keyed by their url, behind a prefix no entity key (`type:id`) can start with. */
const PAGE_KEY_PREFIX = 'page:';

function SearchPanel({
  query,
  onQueryChange,
  filter,
  onFilterChange,
  filters,
  everythingScope,
  placeholder,
  emptyHint,
  canAskAi,
  teamDetails,
  onTeamDetailsChange,
  onOpenHit,
  onOpenPage,
  onAsk,
}: SearchPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const types = filters.find(option => option.id === filter)?.types ?? everythingScope;
  const { searchOptions, preprocessedSearchInput, isDebouncing, basicQuery, semanticQuery, errorMessage } =
    useEntitySearch(query, types, {
      allResultsEnabled: true,
      /*
       * Show what the backend found at two characters. The filter this switches off keeps only hits
       * whose name or first word *equals* the query, which for two letters is usually none of them,
       * so a search the backend answered with thirty hits would render as "nothing matches"
       * (PLT-6813). Ranking is the backend's job here, and a loose hit is one keystroke from narrowed.
       */
      skipShortQueryFilter: true,
    });

  const isSearchTriggered = isSearchTriggeredOnInput(preprocessedSearchInput);
  const isSearching = isSearchTriggered && (isDebouncing || basicQuery.isFetching || semanticQuery.isFetching);
  /*
   * The selected chip's example searches, offered while the field is empty, so a reader who opens
   * the panel has something to pick before they know a name. A chip with no examples shows the hint.
   * The hook runs for every chip, as hooks must, and a chip without a list discards its answer.
   */
  const exampleSelection = exampleSearchSelection(filter);
  const suggestions = useSuggestions(exampleSelection ?? AdditionalSearchType.ALL);
  const examples = preprocessedSearchInput === '' && exampleSelection !== null ? suggestions.map(exampleHit) : [];
  const groups = useMemo(
    () => (isSearchTriggered ? groupSearchHits(searchOptions) : []),
    [isSearchTriggered, searchOptions],
  );
  // Internal pages are matched here, from the menu's own list, so they answer from the first keystroke.
  const pages = useMemo(
    () => (teamDetails === true && filter === 'all' ? internalPageHits(startMenuCategories, query) : []),
    [teamDetails, filter, query],
  );
  const hasResults = groups.length > 0 || (isSearchTriggered && pages.length > 0);
  /*
   * No AI row when the search itself failed: its section would say "Nothing matches", which is not
   * what happened, and Enter would hand the text to a chat. The list's empty state says the search
   * failed instead.
   */
  const askPosition =
    canAskAi && !isSearching && !errorMessage
      ? askRowPosition({ query: preprocessedSearchInput, isSearchTriggered, hasResults })
      : null;
  const firstTeamOnlyId = filters.find(option => option.teamOnly === true)?.id;

  function handleAction(key: Key) {
    if (key === ASK_ROW_KEY) {
      onAsk(preprocessedSearchInput);
      return;
    }
    if (typeof key === 'string' && key.startsWith(PAGE_KEY_PREFIX)) {
      const page = pages.find(candidate => candidate.url === key.slice(PAGE_KEY_PREFIX.length));
      if (page !== undefined) {
        onOpenPage(page);
      }
      return;
    }
    const hit = [...examples, ...searchOptions].find(option => hitKey(option) === key);
    if (hit !== undefined) {
      onOpenHit(hit);
    }
  }

  // The first result in the order the list shows them: entity sections first, then internal pages.
  const firstResultKey: Key | undefined =
    groups.length > 0 ? hitKey(groups[0].hits[0]) : pages.length > 0 ? `${PAGE_KEY_PREFIX}${pages[0].url}` : undefined;
  const fallback = enterFallback({
    hasQuery: query.trim() !== '',
    hasResult: firstResultKey !== undefined,
    canAsk: askPosition !== null,
  });
  // What Enter opens with no row highlighted.
  const enterKey = fallback === 'result' ? firstResultKey : fallback === 'ask' ? ASK_ROW_KEY : undefined;

  /*
   * An Enter pressed while a query is in flight, kept with the query it was pressed on. The rows on
   * screen then answer the previous query, so opening one would be a guess; the Enter is acted on
   * when this query's results arrive, and dropped if the reader has typed on in the meantime.
   */
  const pendingEnterQuery = useRef<string | null>(null);
  useEffect(() => {
    const pending = pendingEnterQuery.current;
    if (pending === null || isSearching) {
      return;
    }
    pendingEnterQuery.current = null;
    if (pending === preprocessedSearchInput && enterKey !== undefined) {
      handleAction(enterKey);
    }
  });

  /*
   * Enter in the field opens the highlighted row, and the first result when none is highlighted
   * (design/40-shell-and-pages.md, "Top bar"; `enterFallback` says which row that is).
   * `Autocomplete` highlights the first row only when the list changes as the reader types, and these
   * results arrive after the typing has stopped, so on its own Enter usually opened nothing. Captured
   * on the wrapper so it runs before the field's own handler.
   */
  function handleKeyDownCapture(event: KeyboardEvent<HTMLDivElement>) {
    const field = event.target;
    if (
      event.key !== 'Enter' ||
      event.nativeEvent.isComposing ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      !(field instanceof HTMLInputElement)
    ) {
      return;
    }
    if (isSearching) {
      event.preventDefault();
      event.stopPropagation();
      pendingEnterQuery.current = preprocessedSearchInput;
      return;
    }
    if (!field.hasAttribute('aria-activedescendant') && enterKey !== undefined) {
      event.preventDefault();
      event.stopPropagation();
      handleAction(enterKey);
    }
  }

  const hitItem = (hit: ModelEntitySearchResponseItem) => (
    <ListBoxItem key={hitKey(hit)} id={hitKey(hit)} textValue={hit.name} className={styles.row}>
      {teamDetails === true ? <TeamHitRow hit={hit} /> : <HitRow hit={hit} />}
    </ListBoxItem>
  );

  const askSection = (position: 'first' | 'last') => (
    // The section's id differs from its row's: one collection holds both keys, and a shared key made
    // react-aria fold the section into the group before it (found in the PLT-7118 prototype).
    <ListBoxSection id="ask-ai-section" className={clsx(styles.section, position === 'last' && styles.askSectionLast)}>
      {!hasResults && (
        <Header className={styles.sectionHeader}>
          <Text size="sm" height="xs" color="gray-600" weight="semibold">
            {`Nothing matches “${preprocessedSearchInput}”.`}
          </Text>
        </Header>
      )}
      <ListBoxItem id={ASK_ROW_KEY} textValue={`Ask Upright AI: ${preprocessedSearchInput}`} className={styles.row}>
        <AskRow query={preprocessedSearchInput} />
      </ListBoxItem>
    </ListBoxSection>
  );

  return (
    <div className={styles.body} onKeyDownCapture={handleKeyDownCapture}>
      <Autocomplete inputValue={query} onInputChange={onQueryChange}>
        <div className={styles.field}>
          <Input
            ref={inputRef}
            label="Search"
            isLabelVisuallyHidden
            placeholder={placeholder}
            startAdornment={InterfaceSearch}
            autoFocus
          />
          <ToggleButtonGroup
            aria-label="Search for"
            className={styles.filters}
            selectionMode="single"
            disallowEmptySelection
            selectedKeys={[filter]}
            onSelectionChange={keys => {
              // `disallowEmptySelection` keeps one chip on, so this is the newly chosen one.
              const next = keys.values().next().value;
              if (next !== undefined) {
                onFilterChange(next as SearchFilterId);
              }
            }}
          >
            {filters.map(option => (
              <ToggleButton
                key={option.id}
                id={option.id}
                // The first team-only chip opens the team's half of the row, after a divider.
                className={clsx(styles.chip, option.id === firstTeamOnlyId && styles.chipStartsTeamGroup)}
                // A pointer pick hands focus back to the field, so the next keystroke narrows the
                // query rather than landing on the chip. A keyboard user stays in the chip row,
                // where the arrow keys move between chips.
                onPress={event => {
                  if (event.pointerType !== 'keyboard') {
                    inputRef.current?.focus();
                  }
                }}
              >
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>
        {isSearching && !hasResults && pages.length === 0 ? (
          <LoadingRows />
        ) : (
          <ListBox
            className={styles.list}
            aria-label="Search results"
            onAction={handleAction}
            renderEmptyState={() => (
              <div className={styles.empty}>
                <Text size="md" height="xs" color="gray-600">
                  {emptyMessage({ isSearchTriggered, errorMessage, query: preprocessedSearchInput, emptyHint })}
                </Text>
              </div>
            )}
          >
            {askPosition === 'first' && askSection('first')}
            {examples.length > 0 && (
              <ListBoxSection id="examples" className={styles.section}>
                <Header className={styles.sectionHeader}>
                  <Text size="sm" height="xs" color="gray-600" weight="semibold">
                    Example searches
                  </Text>
                </Header>
                {examples.map(hitItem)}
              </ListBoxSection>
            )}
            {groups.map(group => (
              <ListBoxSection key={group.id} id={group.id} className={styles.section}>
                <Header className={styles.sectionHeader}>
                  <Text size="sm" height="xs" color="gray-600" weight="semibold">
                    {group.label}
                  </Text>
                </Header>
                {group.hits.map(hitItem)}
              </ListBoxSection>
            ))}
            {pages.length > 0 && (
              <ListBoxSection id="internal-pages" className={styles.section}>
                <Header className={styles.sectionHeader}>
                  <Text size="sm" height="xs" color="gray-600" weight="semibold">
                    Internal pages
                  </Text>
                </Header>
                {pages.map(page => (
                  <ListBoxItem
                    key={page.url}
                    id={`${PAGE_KEY_PREFIX}${page.url}`}
                    textValue={page.label}
                    className={styles.row}
                  >
                    <PageRow page={page} />
                  </ListBoxItem>
                ))}
              </ListBoxSection>
            )}
            {askPosition === 'last' && askSection('last')}
          </ListBox>
        )}
      </Autocomplete>
      <div className={clsx(styles.footer, teamDetails !== undefined && styles.footerTeam)}>
        <span className={styles.hints}>
          <KeyHint keys={['↑', '↓']} label="Move" />
          <KeyHint keys={['Enter']} label="Open" />
          <KeyHint keys={['Esc']} label="Close" />
        </span>
        <span className={styles.footerEnd}>
          <span className={styles.shortcut}>
            <Text size="sm" height="xs" color="gray-600">
              ⌘K or Ctrl+K opens search
            </Text>
          </span>
          {teamDetails !== undefined && (
            <Checkbox isSelected={teamDetails} onChange={onTeamDetailsChange}>
              {/* The footer's own step, so the switch reads as part of the footer rather than above it. */}
              <Text size="sm" height="xs" color="gray-800">
                Team details
              </Text>
            </Checkbox>
          )}
        </span>
      </div>
    </div>
  );
}

/**
 * What the list says when it has no rows: what the panel searches, before anything searchable is
 * typed; the failure, when the search failed; otherwise that nothing matches, which is only reached
 * without the AI row (with it, the row's own section says so).
 */
function emptyMessage({
  isSearchTriggered,
  errorMessage,
  query,
  emptyHint,
}: {
  isSearchTriggered: boolean;
  errorMessage: string | null;
  query: string;
  emptyHint: string;
}) {
  if (!isSearchTriggered) {
    return emptyHint;
  }
  if (errorMessage) {
    return `Search failed: ${errorMessage}`;
  }
  return `Nothing matches “${query}”.`;
}

/**
 * One result: the type icon, the name in semibold, then the short facts that tell same-named
 * entities apart, in the grammar the top bar's dropdown used. The name is one weight, not the
 * matched substring picked out of it: every row matches, so highlighting the match emphasises the
 * word they share and leaves the eye off the fact that separates them.
 */
function HitRow({ hit }: { hit: ModelEntitySearchResponseItem }) {
  const Icon = getModelEntitySvgIcon(hit.type);
  const facts = factsAfterName(hit);
  return (
    <>
      <span className={styles.icon}>
        <Icon className={styles.svg} aria-hidden="true" />
      </span>
      <span className={styles.main}>
        <Text size="md" height="none" color="gray-900" weight="semibold" truncate>
          {hit.name}
        </Text>
        {facts ? (
          <Text size="md" height="none" color="gray-600" truncate>
            {facts}
          </Text>
        ) : null}
      </span>
    </>
  );
}

/**
 * The team's row (PLT-7169): every fact the search returns, in the order the legacy dark-header row
 * gives them, so nothing the team reads there is lost. After the name, the description where there
 * is one, else the country. At the end, the type with the facts that qualify it (a company's
 * product-set source, a fund's universe, a legal entity's modelling company), then the id, which is
 * the product code for a product. The id gives way first and keeps its whole value in its title.
 */
function TeamHitRow({ hit }: { hit: ModelEntitySearchResponseItem }) {
  const Icon = getModelEntitySvgIcon(hit.type);
  const country = hit.country ? uppercaseFirstLetter(friendlyCountryName(hit.country)) : undefined;
  const detail = hit.description ? uppercaseFirstLetter(hit.description) : country;
  return (
    <>
      <span className={styles.icon}>
        <Icon className={styles.svg} aria-hidden="true" />
      </span>
      <span className={styles.main}>
        <Text size="md" height="none" color="gray-900" weight="semibold" truncate>
          {hit.name}
        </Text>
        {detail ? (
          <Text size="md" height="none" color="gray-600" truncate>
            {detail}
          </Text>
        ) : null}
      </span>
      <span className={clsx(styles.end, styles.teamFacts)}>
        <Text size="sm" height="none" color="gray-600">
          {teamTypePhrase(hit)}
        </Text>
        <Text size="sm" height="none" color="gray-600" family="monospace" truncate>
          {hit.id}
        </Text>
      </span>
    </>
  );
}

/** "Company · Manual", "Mutual fund · User import", "Legal entity · Modelled by Acme Corp", "Product". */
function teamTypePhrase(hit: ModelEntitySearchResponseItem) {
  if (hit.modelledBy) {
    return `Legal entity · Modelled by ${hit.modelledBy.name}`;
  }
  const type =
    hit.type === ModelEntityType.GROUP && hit.groupType
      ? capitalize(friendlyGroupType(hit.groupType))
      : hit.type === ModelEntityType.COMPANY && hit.companyType
        ? capitalize(friendlyCompanyType(hit.companyType))
        : capitalize(friendlyModelEntityType(hit.type));
  const qualifier =
    hit.type === ModelEntityType.COMPANY && hit.fuzzyProductSetQuality
      ? friendlyProductSetQuality(hit.fuzzyProductSetQuality as CompanyProductSetQuality)
      : fundUniverse(hit.accessSpace);
  return [type, qualifier].filter((part): part is string => !!part).join(' · ');
}

/** Which universe a fund comes from: the legacy row's "Upright database" or "User import". */
function fundUniverse(accessSpace: AccessSpace | undefined) {
  switch (accessSpace) {
    case AccessSpace.SHARED:
    case AccessSpace.PROMISED_PUBLIC:
    case AccessSpace.SEMIPRIVATE:
      return 'Upright database';
    case AccessSpace.PRIVATE:
      return 'User import';
    case undefined:
      return undefined;
  }
}

/**
 * An internal page: a window icon, the one kind of row that is a place in the app rather than a
 * thing in the data; the page's name from the internal-tools menu; and where the menu keeps it.
 */
function PageRow({ page }: { page: InternalPageHit }) {
  return (
    <>
      <span className={styles.icon}>
        <LayoutWindow10 className={styles.svg} aria-hidden="true" />
      </span>
      <span className={styles.main}>
        <Text size="md" height="none" color="gray-900" weight="semibold" truncate>
          {page.label}
        </Text>
        <Text size="md" height="none" color="gray-600" truncate>
          {page.location}
        </Text>
      </span>
      <span className={clsx(styles.end, styles.teamFacts)}>
        <Text size="sm" height="none" color="gray-600" family="monospace" truncate>
          {page.external ? 'Opens in a new tab' : page.url}
        </Text>
      </span>
    </>
  );
}

/**
 * The facts after the name, in one order for every row: where it is (or what it is, for the
 * product-like types that carry a description instead), then what kind of thing it is where that is
 * worth a word.
 */
function factsAfterName(hit: ModelEntitySearchResponseItem) {
  const country = hit.country ? uppercaseFirstLetter(friendlyCountryName(hit.country)) : undefined;
  // A name that already contains its country does not need it repeated ("Bank of Ireland · Ireland").
  const place = country && !hit.name.toLowerCase().includes(country.toLowerCase()) ? country : undefined;
  const detail = place ?? (hit.description ? uppercaseFirstLetter(hit.description) : undefined);
  return [detail, typeLabel(hit)].filter((part): part is string => !!part).join(' · ');
}

/**
 * The fund or company subtype, where the backend sent one that is not just the entity type again.
 * The bare entity type is left to the row's icon and its section heading: spelling it out ends every
 * row of a common-name search in the same dead word.
 */
function typeLabel(hit: ModelEntitySearchResponseItem) {
  if (hit.type === ModelEntityType.GROUP && hit.groupType) {
    return capitalize(friendlyGroupType(hit.groupType));
  }
  // `CompanyType.COMPANY` friendlies to "company", i.e. the bare entity type reached another way.
  if (hit.type === ModelEntityType.COMPANY && hit.companyType && hit.companyType !== CompanyType.COMPANY) {
    return capitalize(friendlyCompanyType(hit.companyType));
  }
  return undefined;
}

/**
 * The AI entry point: the yellow sparkle disc that marks every AI entry point (DESIGN.md rule 6),
 * then the text it would hand over. Choosing it opens a new chat with that text as the first
 * message; nothing runs before that, so the search itself has no AI cost.
 */
function AskRow({ query }: { query: string }) {
  return (
    <>
      <span className={styles.askMark}>
        <AiSparklesIcon width={12} height={12} aria-hidden="true" />
      </span>
      <span className={styles.main}>
        <Text size="md" height="none" color="gray-900" weight="semibold">
          Ask Upright AI
        </Text>
        <Text size="md" height="none" color="gray-600" truncate>
          {`“${query}”`}
        </Text>
      </span>
      <span className={styles.end}>
        <Text size="sm" height="none" color="gray-600">
          Opens a new chat
        </Text>
      </span>
    </>
  );
}

/** Results in flight: skeleton rows hold the list open at about the height results take, so the panel does not jump. */
function LoadingRows() {
  return (
    <SkeletonProvider isLoading>
      <div className={styles.loading} aria-busy="true" aria-label="Searching">
        {['first', 'second', 'third', 'fourth'].map(row => (
          <Skeleton key={row} minBlockSize="2rem">
            <div>&nbsp;</div>
          </Skeleton>
        ))}
      </div>
    </SkeletonProvider>
  );
}

function KeyHint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <span className={styles.hint}>
      {keys.map(key => (
        <kbd key={key} className={styles.key}>
          {key}
        </kbd>
      ))}
      <Text size="sm" height="xs" color="gray-600">
        {label}
      </Text>
    </span>
  );
}
